import { FC, useMemo, useState } from 'react';
import { Avatar, Button, Col, Input, Row } from 'antd';
import { TbCircleXFilled, TbLayoutGrid, TbSearch, TbUsers, TbUsersMinus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { EmptySection, VirtualizedList } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useTeamStore } from '@/stores';
import { Team } from '@/types';

// interface JoinProps {}

const Join: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();

    const [input, setInput] = useState('');
    const [joiningTeamId, setJoiningTeamId] = useState<string | null>(null);

    const { hasMinimumPlan } = useEntitlements();
    const { teams, joinTeam } = useTeamStore();

    const joinableTeams = useMemo(() => {
        return teams.filter((team) => {
            if (!team.canJoin) {
                return false;
            }

            if (!input.trim()) {
                return true;
            }

            return team.name?.toLowerCase().includes(input.trim().toLowerCase());
        });
    }, [input, teams]);

    const handleJoinTeam = async (team: Team) => {
        setJoiningTeamId(team.id);

        try {
            await joinTeam(team.id);
            toast('Team Joined', { description: team.name });
        } finally {
            setJoiningTeamId(null);
        }
    };

    return (
        <>
            <Input
                disabled={!hasMinimumPlan('plus')}
                prefix={<TbSearch size='1rem' style={{ marginRight: '0.5rem', color: token.colorTextQuaternary }} />}
                placeholder='Search Teams To Join...'
                value={input}
                onChange={(event) => setInput(event.target.value)}
                allowClear={{ clearIcon: <TbCircleXFilled size='1rem' style={{ verticalAlign: '-0.2rem' }} /> }}
            />
            {joinableTeams?.length !== 0 ? (
                <VirtualizedList
                    items={joinableTeams}
                    height='17.5rem'
                    estimateSize={68}
                    getItemKey={(team) => team.id}
                    style={{ marginTop: '0.75rem', borderTop: `1px solid ${token.colorBorder}` }}
                    renderItem={(team) => (
                        <Row
                            key={team.id}
                            align='middle'
                            wrap={false}
                            style={{ borderBottom: `1px solid ${token.colorBorderSecondary}`, padding: '0.75rem 0' }}
                        >
                            <Col flex='none'>
                                <Avatar
                                    shape='square'
                                    icon={team.emoji || <TbUsers size='1rem' />}
                                    style={{ marginRight: '0.75rem' }}
                                />
                            </Col>
                            <Col flex='auto'>
                                <div style={{ fontWeight: 500 }}>{team.name}</div>
                                <small style={{ color: token.colorTextTertiary }}>
                                    <TbLayoutGrid style={{ verticalAlign: '-0.125rem', marginRight: '0.25rem' }} />
                                    Workspace · {team.teammateCount ?? team.teammates?.length ?? 0} members
                                </small>
                            </Col>
                            <Col flex='none'>
                                <Button
                                    type='primary'
                                    loading={joiningTeamId === team.id}
                                    disabled={!!joiningTeamId}
                                    onClick={() => handleJoinTeam(team)}
                                >
                                    Join
                                </Button>
                            </Col>
                        </Row>
                    )}
                />
            ) : (
                <EmptySection
                    description={
                        input.trim()
                            ? 'No joinable teams match your search.'
                            : 'There are no teams available for you to join.'
                    }
                    height='17.5rem'
                    icon={<TbUsersMinus size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                    label='No Teams Available to Join'
                />
            )}
        </>
    );
};

export default Join;
